import PropTypes from 'prop-types';
import clsx from 'clsx';
import _, { isNull, isUndefined } from 'lodash';
import { useRouter } from 'next/router';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { FaTimes } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';
import {
  FlightFiltersOptions, FlightSearchTypes, InitialFlightFilters, MaximumFlightSegments, SearchModes,
  HomeSearchInputFormVariants, SearchStatus,
  TravelTypes,
  InitialFormValidations,
} from '../../../../constants';
import useSearchObjFromQueryString from '../../../../hooks/useSearchObjFromQueryString';
import {
  flightSearch,
  initDirectSellFlightSearchRequest, initFlightSearchRequest,
} from '../../../../store/slices/flight';
import { resetTrips } from '../../../../store/slices/trip';
import {
  setFlightSearchObjFromQueryString,
  resetSearchResults,
  setTripSearchInputs,
  setFlightTripSearchInputs,
  setFlightTripSearchParam,
} from '../../../../store/slices/tripSearch';
import setURLParams from '../../../../utils/setURLParams';
import CustomerLookup from '../../../CustomerLookup';
import ErrorTooltip from '../../../ErrorTooltip';
import ToolTip from '../../../ToolTip';
import AgentPreferencesFilter from '../../../TravelOption/FlightInformation/FlightFilterBar/AgentPreferencesFilter';
import CabinClassFilter from '../../../TravelOption/FlightInformation/FlightSearchBar/CabinClassFilter';
import FlightSearchTypesFilter from '../../../TravelOption/FlightInformation/FlightFilterBar/FlightSearchTypesFilter';
import FromFlightFilter from '../../../TravelOption/FlightInformation/FlightFilterBar/FromFlightFilter';
import LeaveDateFlightFilter from '../../../TravelOption/FlightInformation/FlightFilterBar/LeaveDateFlightFilter';
import RateCodePreferencesFilter from '../../../TravelOption/FlightInformation/FlightFilterBar/RateCodePreferencesFilter';
import ReturnDateFlightFilter from '../../../TravelOption/FlightInformation/FlightFilterBar/ReturnDateFlightFilter';
import FlightStops from '../../../TravelOption/FlightInformation/FlightFilterBar/FlightStops';
import PassengersFilter from '../../FilterBar/PassengersFilter';
import AirlinesLookup from '../../../TravelOption/FlightInformation/FlightSearchBar/AirlinesLookup';
import FlightTimeSlidersInput from '../../../TravelOption/FlightInformation/FlightSearchBar/FlightTimeSlidersInput';
import encodeStateroomsObj from '../../../../utils/urlEncode/encodeStateroomsObj';
import checkIsFlightInputsChanged from '../../../../helpers/checkIsFlightInputsChanged';
import LayoverDurationInput from '../../../TravelOption/FlightInformation/FlightSearchBar/LayoverDurationInput';
import { checkDoEnableSelectFlightsBySegment, getTravelPolicyLeadDays } from '../../../../store/slices/user';
import checkIfNewFlightSearch from '../../../../helpers/validationHelpers/checkIfNewFlightSearch';
import validateFlightSearchInputs from './validateFlightSearchInputs';
import flightSearchInputSelector from '../../../../helpers/selectors/flightSearchInput';
import checkIfInBoundFlightInputsChanged from '../../../../helpers/checkIfInBoundFlightInputsChanged';
import AlliancesLookup from '../../../TravelOption/FlightInformation/FlightSearchBar/AlliancesLookup';
import ConnectingAirports from '../../../TravelOption/FlightInformation/FlightSearchBar/ConnectingAirports';
import CarrierCodeFilter from '../../../TravelOption/FlightInformation/FlightFilterBar/CarrierCodeFilter';
import DirectFlightDateFilter from '../../../TravelOption/FlightInformation/FlightFilterBar/DirectFlightDateFilter';
import FlightNumberInput from '../../../TravelOption/FlightInformation/FlightFilterBar/FlightNumberInput';
import CabinInput from '../../../TravelOption/FlightInformation/FlightFilterBar/CabinInput';
import CheckAvailabilityModal from '../../../TravelOption/FlightInformation/FlightFilterBar/CheckAvailabilityModal';

const FlightSearchbar = ({ filterBgColor, searchTypeTextColor, tripId = undefined }) => {
  const isTripSearch = !tripId;
  const ENABLE_CUSTOMER_LOOKUP = false; // Test well when enabling this flag
  const [toolTip, setToolTip] = useState(false);
  const { track } = useAnalytics();
  const [doOpenFirstDatePicker, setDoOpenFirstDatePicker] = useState(false);
  const [doOpenSecondDatePicker, setDoOpenSecondDatePicker] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(undefined);
  const staterooms = useSelector((store) => store.tripSearch.searchInputs.staterooms);
  const router = useRouter();
  const dispatch = useDispatch();
  const leadDays = useSelector(getTravelPolicyLeadDays(TravelTypes.flight));
  const { isLoggedIn, flightRateCodes, travelAgency } = useSelector((store) => store.user);
  const doHidePostSearchFilters = travelAgency && travelAgency.settings
    && travelAgency.settings.doHidePostSearchFilters;
  const searchInputs = useSelector((store) => store.tripSearch.searchInputs);
  const { flight: flightSearchInput = {} } = searchInputs;
  const searchObj = useSelector((store) => store.tripSearch.searchObj);

  const {
    breadCrumbsIndex, selectedFlightSegments,
  } = useSelector(flightSearchInputSelector(tripId, true));
  const { flight: searchObjInput = {} } = searchObj;
  const selectedSearchMode = useSelector((store) => store.tripSearch.selectedSearchMode);
  const searchStatus = useSelector((store) => store.tripSearch.searchStatus);
  const { searchMode: searchModeQuery } = router.query;
  const homeSearchInputFormVariant = travelAgency
    && travelAgency.siteTheme && travelAgency.siteTheme.homeSearchInputFormVariant;
  const doCenterAlignFilters = !isLoggedIn
    && homeSearchInputFormVariant === HomeSearchInputFormVariants.CENTER_ALIGNED;
  const gapBetweenSearchbarFields = travelAgency && travelAgency.siteTheme
    && travelAgency.siteTheme.gapBetweenSearchbarFields;
  const doShowGradientOnSearchBtn = travelAgency && travelAgency.siteTheme
    && travelAgency.siteTheme.showGradientOnSearchBtn;
  const doEnableSelectFlightsBySegment = useSelector(checkDoEnableSelectFlightsBySegment);
  const [formValidations, setFormValidations] = useState(InitialFormValidations);
  const isDirectSell = useMemo(
    () => flightSearchInput.flightSearchType === FlightSearchTypes.DIRECT_SELL,
    [flightSearchInput.flightSearchType],
  );

  const isRoundTrip = useMemo(
    () => flightSearchInput.flightSearchType === FlightSearchTypes.ROUND_TRIP,
    [flightSearchInput.flightSearchType],
  );

  const isMultiCity = useMemo(
    () => flightSearchInput.flightSearchType === FlightSearchTypes.MULTI_CITY,
    [flightSearchInput.flightSearchType],
  );

  const isOneWay = useMemo(
    () => flightSearchInput.flightSearchType === FlightSearchTypes.ONE_WAY,
    [flightSearchInput.flightSearchType],
  );

  const handleSearch = (event, doShowValidationErrors = true) => {
    setToolTip(false);

    if (event) event.preventDefault();
    if (validateFlightSearchInputs(setFormValidations, leadDays, flightSearchInput.flightSearchType,
      flightSearchInput, doShowValidationErrors)) {
      if (isDirectSell) {
        dispatch(initDirectSellFlightSearchRequest({ isTripSearch: true }));
        setURLParams({
          searchObj: searchInputs,
          searchedMode: SearchModes.FLIGHT,
          router,
        });
        dispatch(resetTrips());
        track('flightSearch (direct-sell)', searchInputs && searchInputs.flight ? {
          segments: searchInputs.flight.flightSegments && searchInputs.flight.flightSegments.length
            ? _.map(searchInputs.flight.flightSegments, (f) => ({
              origin: f.origin ? f.origin.label : undefined,
              destination: f.origin ? f.destination.label : undefined,
              departureDate: f.departureDate,
              flightNumber: f.flightNumber,
              cabin: f.cabin,
              carrierCode: f.carrierCode?.value,
            })) : undefined,
          flightSearchType: searchInputs.flight.flightSearchType,
          passengers: searchInputs.staterooms && encodeStateroomsObj(searchInputs.staterooms),
        } : {});
        return;
      }
      const payload = {
        ...flightSearchInput,
        ...InitialFlightFilters,
        isTripSearch: true,
      };
      if (doEnableSelectFlightsBySegment) {
        const inboundFlightSegmentChangeIndex = checkIfInBoundFlightInputsChanged({
          searchInputs: searchInputs.flight,
          searchObject: searchObjInput,
        });
        const isThereChange = checkIsFlightInputsChanged({
          searchInputs: {
            ...searchInputs.flight,
            staterooms: searchInputs.staterooms,
          },
          compareBase: {
            ...searchObjInput,
            staterooms: searchObj.staterooms,
          },
        });
        if (breadCrumbsIndex === 0 || (breadCrumbsIndex !== 0 && !isThereChange)
          || inboundFlightSegmentChangeIndex <= 0) {
          payload.selectedFlightSegments = {};
          payload.breadCrumbsIndex = 0;
        } else {
          delete payload.selectedFlightSegments;
          delete payload.breadCrumbsIndex;
        }
        if (breadCrumbsIndex !== inboundFlightSegmentChangeIndex
          && inboundFlightSegmentChangeIndex > 0 && selectedFlightSegments[breadCrumbsIndex]) {
          dispatch(setFlightTripSearchInputs({
            breadCrumbsIndex:
              inboundFlightSegmentChangeIndex,
          }));
        }
      }

      payload.isNewSearch = checkIfNewFlightSearch(searchInputs, searchObj);
      dispatch(resetTrips());
      dispatch(setFlightTripSearchParam({ staterooms: searchInputs.staterooms }));
      dispatch(setFlightTripSearchInputs({
        ...InitialFlightFilters,
        hasUserInteractedWithFlightList: false,
        foundTotalFlights: undefined,
        searchProgress: undefined,
      }));

      dispatch(initFlightSearchRequest(payload));

      setURLParams({
        searchObj: searchInputs,
        searchedMode: SearchModes.FLIGHT,
        router,
      });
      track('flightSearch', searchInputs && searchInputs.flight ? {
        segments: searchInputs.flight.flightSegments && searchInputs.flight.flightSegments.length
          ? _.map(searchInputs.flight.flightSegments, (f) => ({
            origin: f.origin ? f.origin.label : undefined,
            destination: f.origin ? f.destination.label : undefined,
            flightTime: f.flightTime,
            leaveDate: f.leaveDate,
            preferredCabin: f.preferredCabin,
            connectingAirportsPreference: f.connectingAirportsPreference,
            connectingAirports: f.connectingAirports,
          })) : undefined,
        flightSearchType: searchInputs.flight.flightSearchType,
        airlines: searchInputs.flight.airlines && _.map(searchInputs.flight.airlines, 'value'),
        alliances: searchInputs.flight.alliances && _.map(searchInputs.flight.alliances, 'value'),
        airlinesPreference: searchInputs.flight.airlinesPreference,
        alliancePreference: searchInputs.flight.alliancePreference,
        maxNumOfStops: searchInputs.flight.maxNumOfStops,
        passengers: searchInputs.staterooms && encodeStateroomsObj(searchInputs.staterooms),
      } : {});
    }
  };

  const handleChangeCustomer = useCallback((customerInfo) => {
    const updatedStaterooms = Array.isArray(staterooms) ? [...staterooms] : [];
    if (!updatedStaterooms.length) updatedStaterooms.push({});
    updatedStaterooms[0] = {
      ...updatedStaterooms[0],
      customerId: customerInfo && customerInfo.customerId
        ? customerInfo.customerId : undefined,
      customerEmail: customerInfo && customerInfo.email
        ? customerInfo.email : undefined,
    };
    dispatch(setTripSearchInputs({ staterooms: updatedStaterooms }));
    dispatch(setFlightTripSearchParam({ staterooms: updatedStaterooms }));
  }, [dispatch, staterooms]);

  const handleResetValidation = () => {
    setFormValidations(InitialFormValidations);
  };

  const handleOnClose = () => {
    setToolTip(false);
  };

  const handleOpenFirstDatePicker = useCallback((val) => {
    setDoOpenFirstDatePicker(val);
    if (!val) {
      setTimeout(() => { setDoOpenSecondDatePicker(true); }, 50);
    }
  }, []);

  useEffect(() => {
    if (doOpenFirstDatePicker) setDoOpenSecondDatePicker(false);
  }, [doOpenFirstDatePicker]);

  useSearchObjFromQueryString((searchObjFromQueryString) => {
    if (searchStatus !== SearchStatus.INITIAL) return;
    if ((searchModeQuery || SearchModes.CRUISE_TRIP) === selectedSearchMode) {
      dispatch(setFlightSearchObjFromQueryString(searchObjFromQueryString));
      if (validateFlightSearchInputs(setFormValidations, leadDays,
        searchObjFromQueryString.flightSearchType, searchObjFromQueryString, false)) {
        if (searchObjFromQueryString.flightSearchType === FlightSearchTypes.DIRECT_SELL) {
          dispatch(initDirectSellFlightSearchRequest({ isTripSearch: true }));
        } else {
          dispatch(flightSearch({ isTripSearch: true }));
        }
      }
    } else {
      dispatch(resetSearchResults({ isLoggedIn }));
      handleSearch(null, false);
    }
  }, undefined);

  useEffect(() => {
    if (flightRateCodes && flightRateCodes.length > 0
      && flightSearchInput.rateCodes === undefined) {
      const filteredRateCodes = _.filter(flightRateCodes, (r) => r.isSelectedByDefault);
      if (filteredRateCodes.length > 0) dispatch(setFlightTripSearchInputs({ rateCodes: _.map(filteredRateCodes, 'rateCode') }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightRateCodes]);

  const handleAddAnotherFlight = useCallback((event) => {
    if (event) event.preventDefault();
    if (flightSearchInput.flightSegments.length < MaximumFlightSegments) {
      const payload = {
        tripId,
        searchObj: {
          flightSegments: [...flightSearchInput.flightSegments, {}],
        },
      };
      dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    }
  }, [dispatch, flightSearchInput.flightSegments, tripId]);

  const handleClearFlightChoice = useCallback((event) => {
    if (event) event.preventDefault();
    const payload = {
      searchObj: {
        flightSegments: isDirectSell ? [{}] : [{}, {}],
      },
    };
    dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
  }, [dispatch, isDirectSell]);

  const handleRemoveFlightSegmentRow = useCallback((event, rowIndex) => {
    if (event) event.preventDefault();
    const minSegments = isDirectSell ? 1 : 2;
    if ((isMultiCity
      || isDirectSell)
      && flightSearchInput.flightSegments.length > minSegments) {
      const currentFlightSegments = [...flightSearchInput.flightSegments];
      _.remove(currentFlightSegments, (value, index) => index === rowIndex);
      const payload = {
        searchObj: {
          flightSegments: currentFlightSegments,
        },
      };
      dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    }
  }, [dispatch, isDirectSell, flightSearchInput.flightSegments, isMultiCity]);

  const handleOpenMultiCityDatePicker = useCallback((isOpen, index) => {
    setDatePickerOpen(isOpen ? index : undefined);
    if (!isOpen && index < (flightSearchInput.flightSegments || []).length - 1) {
      setDatePickerOpen(index + 1);
    }
  }, [flightSearchInput.flightSegments]);

  const isAddFlightSegmentDisabled = useMemo(() => flightSearchInput.flightSegments
    && flightSearchInput.flightSegments.length === MaximumFlightSegments,
  [flightSearchInput.flightSegments]);

  useEffect(() => {
    if (!doHidePostSearchFilters || searchStatus !== SearchStatus.DONE) return;
    const areFiltersDifferent = checkIsFlightInputsChanged({
      searchInputs: {
        ...searchInputs.flight,
        staterooms: searchInputs.staterooms,
      },
      compareBase: {
        ...searchObjInput,
        staterooms: searchObj.staterooms,
      },
    });
    setToolTip(areFiltersDifferent);
  }, [searchObjInput, searchObj, searchInputs, doHidePostSearchFilters,
    searchStatus, isDirectSell]);

  const checkDoOpenDatePicker = useCallback((currentIndex) => ((isRoundTrip || isOneWay)
    ? doOpenFirstDatePicker
    : datePickerOpen === currentIndex),
  [datePickerOpen, doOpenFirstDatePicker, isRoundTrip, isOneWay]);

  const isSmallScreen = useMediaQuery({ query: '(max-width: 768px)' });
  const renderDirectSellFields = (currentIndex) => {
    const validation = formValidations.flightSegments[currentIndex] || {};
    // const currentSegment = flightSearchInput?.flightSegments[currentIndex] || {};
    const prevSegment = flightSearchInput?.flightSegments[currentIndex - 1];
    return (
      <>
        <ErrorTooltip
          title={validation.departureDate || ''}
          open={!_.isEmpty(validation.departureDate)}
          arrow
        >
          <div className="col-span-12 md:col-span-2">
            <DirectFlightDateFilter
              dateFilterType="departureDate"
              currentIndex={currentIndex}
              label="Departure date"
              variant={isSmallScreen ? 'outline' : 'outline-no-radius'}
              minDate={prevSegment?.departureDate}
            />
          </div>
        </ErrorTooltip>
        {/* <ErrorTooltip
          title={validation.arrivalDate || ''}
          open={!_.isEmpty(validation.arrivalDate)}
          arrow
        >
          <div className="col-span-12 md:col-span-2">
            <DirectFlightDateFilter
              dateFilterType="arrivalDate"
              currentIndex={currentIndex}
              label="Arrival date"
              minDate={currentSegment?.departureDate}
              variant={isSmallScreen ? 'outline' : 'outline-no-radius'}
            />
          </div>
        </ErrorTooltip> */}
        <div className="col-span-12 md:col-span-4 flex flex-col md:flex-row gap-2 md:gap-0">
          <ErrorTooltip
            title={validation.carrierCode || ''}
            open={!_.isEmpty(validation.carrierCode)}
            arrow
          >
            <div className="flex-1 basis-1 min-w-0">
              <CarrierCodeFilter
                variant={isSmallScreen ? 'outline' : 'outline-no-radius'}
                currentIndex={currentIndex}
              />
            </div>
          </ErrorTooltip>

          <ErrorTooltip
            title={validation.flightNumber || ''}
            open={!_.isEmpty(validation.flightNumber)}
            arrow
          >
            <div className="flex-1 basis-0 min-w-0">
              <FlightNumberInput
                currentIndex={currentIndex}
                variant={isSmallScreen ? 'rounded-2xl h-12' : ''}
              />
            </div>
          </ErrorTooltip>

          <ErrorTooltip
            title={validation.cabin || ''}
            open={!_.isEmpty(validation.cabin)}
            arrow
          >
            <div className="flex-1 basis-0 min-w-0">
              <CabinInput currentIndex={currentIndex} variant={isSmallScreen ? 'rounded-2xl h-12' : ''} />
            </div>
          </ErrorTooltip>
        </div>
      </>
    );
  };

  const SearchButton = () => {
    const wrapperClass = isDirectSell ? 'w-full mr-3 pr-5 h-12 md:mr-0 md:pr-0 md:w-32' : 'col-span-12 md:col-span-2 h-12 md:h-full';
    const buttonClass = clsx(
      'bg-searchButton-0 text-searchbtn-0 font-bold text-center w-full h-full',
      'rounded-2xl',
      {
        'md:rounded-r-2xl md:rounded-l-none': !isDirectSell,
        'hover:gradient-animation': doShowGradientOnSearchBtn,
      },
    );

    return (
      <div className={wrapperClass}>
        <ToolTip
          zIndex={1}
          onClose={handleOnClose}
          title="Please click Search to apply the newly changed parameters"
          open={toolTip}
          placement={(isDirectSell || isMultiCity) ? 'bottom' : 'top'}
        >
          <button type="submit" className={buttonClass}>
            <span className="font-body">Search</span>
          </button>
        </ToolTip>
      </div>
    );
  };

  const getVariant = (index) => {
    if (isSmallScreen) return 'outline';
    return index > 0 ? 'outline-no-radius' : 'outline-left-radius';
  };

  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [availabilitySegment, setAvailabilitySegment] = useState(0);

  // Trigger check availability modal if flight search input is valid
  const handleCheckAvailabilityClick = (showValidationErrors = true, segmentIndex) => {
    const isValid = validateFlightSearchInputs(
      setFormValidations,
      leadDays,
      flightSearchInput.flightSearchType,
      flightSearchInput,
      showValidationErrors,
      true,
      segmentIndex,
    );

    if (isValid) {
      setIsAvailabilityModalOpen(true);
      setAvailabilitySegment(segmentIndex);
    }
    track('checkAvailabilityClick', {
      segmentIndex,
    });
  };

  return (
    <>
      <form onSubmit={handleSearch} onFocus={handleResetValidation}>
        <div>
          <FlightSearchTypesFilter textColorClass={searchTypeTextColor} />
          <div className="grid grid-cols-12">
            {flightSearchInput.flightSegments.map((currentItem, currentIndex) => {
              if (isRoundTrip
              && currentIndex === 1) {
                return null;
              }
              const isLastSegment = currentIndex === flightSearchInput.flightSegments.length - 1;
              let leaveDateVariant = '';
              if (isRoundTrip) {
                leaveDateVariant = 'outline-no-radius';
              } else if (!isLastSegment) {
                leaveDateVariant = 'outline-right-radius';
              } else {
                leaveDateVariant = 'outline-no-radius';
              }
              if (isSmallScreen) {
                leaveDateVariant = 'outline';
              }
              return (
                <div className="relative col-span-12" key={`${currentIndex.toString()}`}>
                  <div
                    className={clsx(
                      'col-span-12 grid grid-cols-12 gap-2 md:gap-0',
                      gapBetweenSearchbarFields,
                      {
                        'mb-2': isMultiCity,
                        'relative mb-2': isDirectSell,
                      },
                    )}
                  >
                    {FlightFiltersOptions.map((item, index) => (
                      <ErrorTooltip
                        title={(formValidations.flightSegments[currentIndex]
                      && formValidations.flightSegments[currentIndex][item.selectedItemName]) || ''}
                        open={!isUndefined(formValidations.flightSegments[currentIndex]
                      && formValidations.flightSegments[currentIndex][item.selectedItemName])}
                        arrow
                        key={`${currentIndex.toString()}-${index.toString()}`}
                      >
                        <div
                          className={clsx(
                            {
                              'col-span-12 md:col-span-4': isMultiCity || isOneWay,
                              'col-span-12 md:col-span-3': isRoundTrip || isDirectSell,
                            },
                          )}
                        >
                          <FromFlightFilter
                            key={tripId + index.toString()}
                            tripId={tripId}
                            type={item.type}
                            autocompleteType={item.autocompleteType}
                            autocompleteResultsType={item.autocompleteResultsType}
                            trackEventName={item.trackEventName}
                            selectedItemName={item.selectedItemName}
                            filterButtonMaxWidth="max-w-full w-full"
                            variant={getVariant(index)}
                            doHighlightOnChange={false}
                            index={currentIndex}
                          />
                        </div>
                      </ErrorTooltip>
                    ))}

                    {!isDirectSell && (
                    <ErrorTooltip
                      title={(formValidations.flightSegments[currentIndex]
                    && formValidations.flightSegments[currentIndex].leaveDate) || ''}
                      open={!isNull(formValidations.flightSegments[currentIndex]
                    && formValidations.flightSegments[currentIndex].leaveDate)}
                      arrow
                    >
                      <div className="col-span-12 md:col-span-2">
                        <LeaveDateFlightFilter
                          flightRowIndex={currentIndex}
                          doOpenDatePicker={checkDoOpenDatePicker(currentIndex)}
                          setDoOpenDatePicker={isMultiCity
                            ? handleOpenMultiCityDatePicker : handleOpenFirstDatePicker}
                          filterButtonMaxWidth="max-w-full w-full"
                          variant={leaveDateVariant}
                        />
                      </div>
                    </ErrorTooltip>
                    )}

                    {isRoundTrip
                  && (
                    <ErrorTooltip
                      title={(formValidations.flightSegments[1] && formValidations.flightSegments[1].returnDate) || ''}
                      open={!isNull(formValidations.flightSegments[1]
                        && formValidations.flightSegments[1].returnDate)}
                      arrow
                    >
                      <div className="col-span-12 md:col-span-2">
                        <ReturnDateFlightFilter
                          filterButtonMaxWidth="max-w-full w-full"
                          variant={`${isSmallScreen ? 'outline' : 'outline-no-radius'}`}
                          flightRowIndex={1}
                          doOpenDatePicker={doOpenSecondDatePicker}
                          setDoOpenDatePicker={setDoOpenSecondDatePicker}
                        />
                      </div>
                    </ErrorTooltip>
                  )}
                    {isDirectSell ? (
                      renderDirectSellFields(currentIndex)
                    ) : null}
                    {((isMultiCity && currentIndex < flightSearchInput
                      .flightSegments.length - 2)) && (
                      <div className="flex mx-4">
                        <button
                          type="button"
                          onClick={(e) => handleRemoveFlightSegmentRow(e, currentIndex)}
                          className="text-white items-center underline focus:outline-none"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {(!isMultiCity
                  || currentIndex === flightSearchInput.flightSegments.length - 1)
                   && !isDirectSell && (SearchButton())}
                    {isDirectSell && (
                    <div className="col-span-12 flex flex-row justify-end items-center">
                      <button
                        onClick={() => handleCheckAvailabilityClick(true, currentIndex)}
                        type="button"
                        className="text-sm underline text-tabsText-0 px-2"
                      >
                        Check Availability
                      </button>
                    </div>
                    )}
                  </div>
                  {((isDirectSell && currentIndex !== 0)) && (
                  <button
                    type="button"
                    onClick={(e) => handleRemoveFlightSegmentRow(e, currentIndex)}
                    className="mx-4  absolute md:top-0 md:right-0 md:mt-4 md:-mr-6 -mt-8 text-tabsText-0 underline focus:outline-none"
                  >
                    <FaTimes />
                  </button>
                  )}
                </div>

              );
            })}
            {(isMultiCity
            || isDirectSell
            ) && (
            <div className="col-span-12 flex flex-col-reverse md:flex-row  items-start md:items-center justify-between text-white">
              <div className="flex gap-6">
                <button type="button" disabled={isAddFlightSegmentDisabled} onClick={handleAddAnotherFlight} className={clsx('text-tabsText-0', { 'opacity-50 cursor-auto': isAddFlightSegmentDisabled })}>+ Add another flight</button>
                <button type="button" onClick={handleClearFlightChoice} className="text-tabsText-0"> Clear all </button>
              </div>

              {isDirectSell
              && (SearchButton())}
            </div>
            )}
          </div>
          <div className={clsx('flex gap-2 flex-wrap text-white', { 'mt-4': !isDirectSell, 'mt-1': isDirectSell, 'justify-center': doCenterAlignFilters })}>
            <PassengersFilter bgClassName={filterBgColor} />
            {!isDirectSell && (
            <>
              <FlightStops tripId={tripId} filterBgColor={filterBgColor} />
              <CabinClassFilter filterBgColor={filterBgColor} filterMaxWidthClass="max-w-96" />
              <ConnectingAirports tripId={tripId} filterBgColor={filterBgColor} />
              {ENABLE_CUSTOMER_LOOKUP && isTripSearch && (
                <CustomerLookup
                  doShowInHeaderFilter
                  onChange={handleChangeCustomer}
                />
              )}
              <LayoverDurationInput tripId={tripId} filterBgColor={filterBgColor} />
              <AirlinesLookup tripId={tripId} filterBgColor={filterBgColor} />
              <FlightTimeSlidersInput tripId={tripId} filterBgColor={filterBgColor} />
              <AgentPreferencesFilter tripId={tripId} filterBgColor={filterBgColor} />
              <RateCodePreferencesFilter tripId={tripId} filterBgColor={filterBgColor} />
              <AlliancesLookup tripId={tripId} filterBgColor={filterBgColor} />
            </>
            )}
          </div>
        </div>
      </form>
      {isDirectSell && (
        <CheckAvailabilityModal
          onClose={() => setIsAvailabilityModalOpen((prev) => !prev)}
          doShow={isAvailabilityModalOpen}
          tripId={tripId}
          currentIndex={availabilitySegment}
        />
      )}
    </>
  );
};
FlightSearchbar.propTypes = {
  tripId: PropTypes.string,
  filterBgColor: PropTypes.string,
  searchTypeTextColor: PropTypes.string,
};

FlightSearchbar.defaultProps = {
  tripId: undefined,
  filterBgColor: undefined,
  searchTypeTextColor: 'text-tabsText-0',
};
export default FlightSearchbar;
